import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TextField, InputAdornment, ToggleButton, ToggleButtonGroup,
  Collapse, IconButton, Tooltip, Select, MenuItem, FormControl,
  InputLabel, Button, Divider, Stack, alpha, Alert, CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Search, ExpandMore, ExpandLess, Refresh, FilterList,
  PhoneAndroid, Storage, ErrorOutline, WarningAmber, InfoOutlined,
  CalendarToday,
} from '@mui/icons-material';
import type { ErrorLogEntry, ErrorSource, ErrorLevel } from '../types/errorLogs';
import { listErrorLogFiles } from '../services/graphql';

// ── Level helpers ─────────────────────────────────────────────────────────────

const LEVEL_COLOR: Record<ErrorLevel, 'error' | 'warning' | 'info'> = {
  ERROR: 'error',
  WARN:  'warning',
  INFO:  'info',
};

const LEVEL_ICON: Record<ErrorLevel, React.ReactNode> = {
  ERROR: <ErrorOutline fontSize="small" />,
  WARN:  <WarningAmber fontSize="small" />,
  INFO:  <InfoOutlined fontSize="small" />,
};

// ── Summary cards ─────────────────────────────────────────────────────────────

function SummaryCards({ logs }: { logs: ErrorLogEntry[] }) {
  const counts = useMemo(() => ({
    total:    logs.length,
    errors:   logs.filter(l => l.level === 'ERROR').length,
    warnings: logs.filter(l => l.level === 'WARN').length,
    frontend: logs.filter(l => l.source === 'frontend').length,
    backend:  logs.filter(l => l.source === 'backend').length,
  }), [logs]);

  const cards = [
    { label: 'Total',    value: counts.total,    color: '#1976d2' },
    { label: 'Errors',   value: counts.errors,   color: '#f44336' },
    { label: 'Warnings', value: counts.warnings, color: '#ff9800' },
    { label: 'Frontend', value: counts.frontend, color: '#9c27b0' },
    { label: 'Backend',  value: counts.backend,  color: '#00897b' },
  ];

  return (
    <Grid container spacing={2} mb={3}>
      {cards.map(c => (
        <Grid item xs={6} sm={4} md key={c.label}>
          <Card elevation={0} sx={{ border: '1px solid #dedede' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" color="text.secondary">{c.label}</Typography>
              <Typography variant="h5" fontWeight={600} sx={{ color: c.color }}>{c.value}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// ── Expandable detail row ─────────────────────────────────────────────────────

function DetailRow({ log }: { log: ErrorLogEntry }) {
  const [open, setOpen] = useState(false);

  const isFrontend = log.source === 'frontend';
  const fe = isFrontend ? (log as any) : null;
  const be = !isFrontend ? (log as any) : null;

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          borderLeft: `3px solid`,
          borderLeftColor: log.level === 'ERROR' ? 'error.main' : log.level === 'WARN' ? 'warning.main' : 'info.main',
        }}
        onClick={() => setOpen(o => !o)}
      >
        <TableCell sx={{ width: 32, pr: 0 }}>
          <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
          {new Date(log.timestamp).toLocaleString()}
        </TableCell>

        <TableCell>
          <Chip
            icon={LEVEL_ICON[log.level] as any}
            label={log.level}
            color={LEVEL_COLOR[log.level]}
            size="small"
            variant="outlined"
          />
        </TableCell>

        <TableCell>
          <Chip
            icon={log.source === 'frontend' ? <PhoneAndroid fontSize="small" /> : <Storage fontSize="small" />}
            label={log.source}
            size="small"
            sx={{
              bgcolor: log.source === 'frontend' ? alpha('#9c27b0', 0.1) : alpha('#00897b', 0.1),
              color:   log.source === 'frontend' ? '#9c27b0' : '#00897b',
              border: 'none',
            }}
          />
        </TableCell>

        <TableCell sx={{ maxWidth: 420 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{log.message}</Typography>
          {log.error && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {log.error.name}: {log.error.message}
            </Typography>
          )}
        </TableCell>

        <TableCell>
          {isFrontend ? (
            <Stack direction="row" spacing={0.5}>
              <Chip label={fe.platform} size="small" variant="outlined" />
              <Chip label={`v${fe.appVersion}`} size="small" variant="outlined" />
            </Stack>
          ) : (
            <Typography variant="body2" fontFamily="monospace" fontSize={12}>
              {be.service}
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded detail */}
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                m: 1, p: 2, borderRadius: 1,
                bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
              }}
            >
              <Grid container spacing={2}>
                {/* Left: metadata */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                    METADATA
                  </Typography>
                  <Stack spacing={0.5}>
                    <MetaRow label="Timestamp" value={log.timestamp} />
                    <MetaRow label="Level"     value={log.level} />
                    <MetaRow label="Source"    value={log.source} />
                    {isFrontend && <>
                      <MetaRow label="Platform"   value={fe.platform} />
                      <MetaRow label="App Version" value={fe.appVersion} />
                      {fe.screen     && <MetaRow label="Screen"     value={fe.screen} />}
                      {fe.sessionId  && <MetaRow label="Session ID" value={fe.sessionId} mono />}
                    </>}
                    {!isFrontend && <>
                      <MetaRow label="Service"    value={be.service} />
                      {be.lambdaName && <MetaRow label="Lambda"     value={be.lambdaName} />}
                      {be.requestId  && <MetaRow label="Request ID" value={be.requestId}  mono />}
                      {be.traceId    && <MetaRow label="Trace ID"   value={be.traceId}    mono />}
                      {be.errorCode  && <MetaRow label="Error Code" value={be.errorCode} />}
                    </>}
                  </Stack>
                </Grid>

                {/* Right: stack trace */}
                <Grid item xs={12} md={8}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                    STACK TRACE
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0, p: 1.5, borderRadius: 1,
                      bgcolor: '#1e1e1e', color: '#d4d4d4',
                      fontSize: 11, fontFamily: 'monospace',
                      overflowX: 'auto', whiteSpace: 'pre-wrap',
                      maxHeight: 220, overflowY: 'auto',
                    }}
                  >
                    {(isFrontend ? fe.stackTrace : be.error?.stack) ?? log.error?.stack ?? 'No stack trace available'}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box display="flex" gap={1} alignItems="flex-start">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" fontFamily={mono ? 'monospace' : undefined} sx={{ wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const ROWS_OPTIONS = [10, 25, 50, 100];

const today = () => new Date().toISOString().slice(0, 10);

export default function ErrorLogs() {
  const [source, setSource]           = useState<ErrorSource | 'all'>('all');
  const [level, setLevel]             = useState<ErrorLevel | 'all'>('all');
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dateFrom, setDateFrom]       = useState(today());
  const [dateTo, setDateTo]           = useState(today());

  const [logs, setLogs]       = useState<ErrorLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Reset to page 0 whenever filters change
  const handleSourceChange = (_: any, v: any) => { if (v) { setSource(v); setPage(0); } };
  const handleLevelChange  = (v: ErrorLevel | 'all') => { setLevel(v); setPage(0); };
  const handleSearchChange = (v: string) => { setSearch(v); setPage(0); };
  const handleRowsChange   = (v: number) => { setRowsPerPage(v); setPage(0); };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Get pre-signed URLs from AppSync
      const files = await listErrorLogFiles({ source, dateFrom, dateTo, limit: 200 });

      // 2. Fetch each JSON file directly from S3 (browser → pre-signed URL)
      const allEntries: ErrorLogEntry[] = [];
      await Promise.all(
        files.map(async (file) => {
          try {
            const res = await fetch(file.presignedUrl);
            if (!res.ok) return;
            const entries: ErrorLogEntry[] = (await res.json()).map((e: any) => ({
              ...e,
              level: (e.level as string)?.toUpperCase() as ErrorLevel,
            }));
            allEntries.push(...entries);
          } catch {
            // skip unreadable files
          }
        })
      );

      setLogs(allEntries);
      setPage(0);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [source, dateFrom, dateTo]);

  // Fetch on mount and whenever date/source changes
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = useMemo(() => {
    return logs
      .filter(l => source === 'all' || l.source === source)
      .filter(l => level  === 'all' || l.level  === level)
      .filter(l => {
        if (!search) return true;
        const q = search.toLowerCase();
        const fe = l.source === 'frontend' ? (l as any) : null;
        const be = l.source === 'backend'  ? (l as any) : null;
        return (
          l.message.toLowerCase().includes(q) ||
          l.error?.name?.toLowerCase().includes(q) ||
          l.error?.message?.toLowerCase().includes(q) ||
          fe?.screen?.toLowerCase().includes(q) ||
          fe?.sessionId?.toLowerCase().includes(q) ||
          be?.service?.toLowerCase().includes(q) ||
          be?.requestId?.toLowerCase().includes(q) ||
          be?.traceId?.toLowerCase().includes(q) ||
          be?.errorCode?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        if (b.levelOrder !== a.levelOrder) return b.levelOrder - a.levelOrder;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
  }, [logs, source, level, search]);

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Error Logs</Typography>
          <Typography variant="body2" color="text.secondary">
            Frontend (mobile) and backend (service) error pipeline
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <Refresh />} onClick={fetchLogs} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      <SummaryCards logs={logs} />

      {/* Filters */}
      <Card elevation={0} sx={{ border: '1px solid #dedede', mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap">
            <FilterList fontSize="small" color="action" />

            {/* Date range */}
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarToday fontSize="small" color="action" />
              <TextField
                size="small"
                type="date"
                label="From"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
            </Stack>

            <Divider orientation="vertical" flexItem />

            <ToggleButtonGroup
              value={source}
              exclusive
              size="small"
              onChange={handleSourceChange}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="frontend">
                <PhoneAndroid fontSize="small" sx={{ mr: 0.5 }} /> Frontend
              </ToggleButton>
              <ToggleButton value="backend">
                <Storage fontSize="small" sx={{ mr: 0.5 }} /> Backend
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Level</InputLabel>
              <Select
                value={level}
                label="Level"
                onChange={e => { handleLevelChange(e.target.value as any); }}
              >
                <MenuItem value="all">All levels</MenuItem>
                <MenuItem value="ERROR">ERROR</MenuItem>
                <MenuItem value="WARN">WARN</MenuItem>
                <MenuItem value="INFO">INFO</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search message, service, screen, trace ID…"
              value={search}
              onChange={e => { handleSearchChange(e.target.value); }}
              sx={{ flexGrow: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ border: '1px solid #dedede' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ width: 32 }} />
                <TableCell>Timestamp</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Context</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                    <TableCell><Skeleton width={140} /></TableCell>
                    <TableCell><Skeleton width={60} /></TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width="80%" /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                  </TableRow>
                ))
              ) : paginated.length > 0 ? (
                paginated.map(log => <DetailRow key={log.id} log={log} />)
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {logs.length === 0 ? 'No logs found for the selected date range' : 'No logs match the current filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_OPTIONS}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => handleRowsChange(Number(e.target.value))}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${count} log${count !== 1 ? 's' : ''}`
          }
          labelRowsPerPage="Rows per page:"
          showFirstButton
          showLastButton
        />
      </Card>
    </Box>
  );
}
